<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="users"
      :locale="$i18n.locale"
      :no-data-text="$t('general.admin.tables.no_data_text')"
      :pagination.sync="pagination"
      :loading="$apollo.queries.users.loading"
      :total-items="totalCount"
      class="admin-table"
    >
      <template v-slot:items="props">
        <td>
          {{ props.item.lastname }}, {{ props.item.firstname }}
        </td>
        <td>
          <span v-if="props.item.group_users.length > 0">
            {{ props.item.group_users[0].group.name }}
          </span>
        </td>
        <td>
          {{ props.item.status }}
        </td>
        <td
          class="justify-center align-center layout px-0 three-dots-container"
        >
          <v-menu bottom center min-width="180px" content-class="custom-menu">
            <template v-slot:activator="{ on }">
              <v-btn icon v-on="on">
                <v-icon>more_horiz</v-icon>
              </v-btn>
            </template>

            <v-list>
              <v-list-tile :key="1" @click="editUser(props.item)">
                <v-list-tile-title class="text-xs-center link">
                  {{ $t("general.admin.tables.edit") }}
                </v-list-tile-title>
              </v-list-tile>

              <v-list-tile :key="2" @click="openDeleteDialog(props.item)">
                <v-list-tile-title class="text-xs-center link">
                  {{ $t("general.admin.tables.delete") }}
                </v-list-tile-title>
              </v-list-tile>
            </v-list>
          </v-menu>
        </td>
      </template>
    </v-data-table>

    <v-edit-user-modal
      v-if="newDialog"
      new
      @close="() => newDialog = false"
      @update="onUpdate"
    />

    <v-edit-user-modal
      v-if="editDialog"
      :stored-user="editedUser"
      @close="closeEditDialog"
      @update="onUpdate"
    />

    <v-dialog
      v-model="deleteDialog"
      max-width="900px"
      content-class="custom-dialog"
    >
      <v-card class="pa-5 custom-dialog-card">
        <v-card-title>
          <span class="headline">
            {{ $t("general.dialogs.delete_header_pre") }}
            {{ deletedUser.firstname }} {{ deletedUser.lastname }}
            {{ $t("general.dialogs.delete_header_post") }}
          </span>
        </v-card-title>

        <v-card-text>
          <v-container grid-list-md pa-0>
            <v-layout wrap>
              <v-flex v-show="showFormError" xs12>
                {{ $t("general.dialogs.error") }}
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-btn color="blue darken-1" flat class="cancel" @click="close">
            {{ $t("general.dialogs.cancel") }}
          </v-btn>
          <v-btn color="blue darken-1" flat class="confirm" @click="deleteUser">
            {{ $t("general.dialogs.delete") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-speed-dial bottom right fixed>
      <template v-slot:activator>
        <v-btn
          color="#6480C9"
          dark
          fab
          class="mb-2"
          @click.stop="openNewDialog"
        >
          <v-icon :style="{ lineHeight: '58px' }">add</v-icon>
        </v-btn>
      </template>
    </v-speed-dial>
  </div>
</template>

<script>
// eslint-disable-next-line
import gql from 'graphql-tag';
import VEditUserModal from '@/components/EditUserModal.vue';

export default {
  name: 'UsersTable',

  components: {
    VEditUserModal,
  },

  data() {
    return {
      textInputRules: [value => !!value || this.$t("general.forms.required")],
      deleteDialog: false,
      deletedUser: {},
      editDialog: false,
      editedUser: {},
      newDialog: false,
      showFormError: false,
      users: [],
      pagination: {},
      totalCount: 0,
    };
  },

  watch: {
    dialog(val) {
      return val || this.close();
    },
  },

  created() {
    this.headers = [
      { text: 'Name', value: 'name' },
      { text: 'Firma', value: 'company' },
      { text: 'Status', value: 'status' },
      { text: '', value: '', sortable: false },
    ];
  },

  apollo: {
    companyOwners: {
      query: gql`
        query GetCompanyOwners {
          group_owners {
            groupId
            userId
          }
        }
      `,
      update: data => data.group_owners,
    },
    users: {
      query: gql`
        query GetUsers(
          $limit: Int,
          $offset: Int,
          $order_by: [user_order_by!],
        ) {
          user_aggregate {
            aggregate {
              count
            }
          }
          user(
            limit: $limit,
            offset: $offset,
            order_by: $order_by,
          ) {
            id
            email
            firstname
            lastname
            role
            group_users {
              group {
                id
                name
              }
            }
            project_users {
              project {
                id
                name
              }
            }
          }
        }
      `,
      variables() {
        const { sortBy, descending, page, rowsPerPage } = this.pagination;

        function getSort() {
          if (!sortBy) return null;

          const direction = descending ? 'desc' : 'asc';
          if (sortBy === 'name') {
            return [
              { lastname: direction }, { firstname: direction },
            ];
          }
          return { [sortBy]: direction };
        }

        return {
          limit: rowsPerPage || 10,
          offset: rowsPerPage * (page - 1) || 0,
          order_by: getSort(),
        };
      },
      update(data) {
        this.totalCount = data.user_aggregate.aggregate.count;
        return data.user;
      },
    },
  },

  methods: {
    onUpdate() {
      this.$apollo.queries.users.refetch();
      this.$apollo.queries.companyOwners.refetch();
    },
    openNewDialog() {
      this.newDialog = true;
    },
    openDeleteDialog(item) {
      this.deletedUser = item;
      this.deleteDialog = true;
    },
    isUserCompanyOwner(userId, companyId) {
      return this.companyOwners.some(companyOwner => companyOwner.userId === userId && companyOwner.groupId === companyId);
    },
    editUser(item) {
      this.editedUser = Object.assign({}, item);
      this.editedUser.company =
        item.group_users.length > 0 ? item.group_users[0].group.id : null;
      this.editedUser.projects = item.project_users.map(projectUser => projectUser.project.id);
      this.editedUser.associatedToGroup = !!this.editedUser.company;
      this.editedUser.isAdmin = this.editedUser.role === 'superuser';
      this.editedUser.isCompanyOwner = this.isUserCompanyOwner(item.id, this.editedUser.company);
      this.editDialog = true;
    },
    closeEditDialog() {
      this.editedUser = {};
      this.editDialog = false;
    },
    deleteUser() {
      this.$apollo
        .mutate({
          mutation: gql`
            mutation($id: String!) {
              deleteUser(id: $id)
            }
          `,
          variables: {
            id: this.deletedUser.id,
          },
        })
        .then(() => {
          this.$apollo.queries.users.refetch();
          this.close();
        })
        .catch(() => {
          this.showFormError = true;
        });
    },
    close() {
      this.editDialog = false;
      this.newDialog = false;
      this.deleteDialog = false;
      this.showFormError = false;
      setTimeout(() => {
        this.editedUser = Object.assign({}, this.defaultUser);
        this.newUser = Object.assign({}, this.defaultUser);
        this.deletedUser = Object.assign({}, this.defaultUser);
      }, 300);
    },
  },
};
</script>
